import { useEffect, useRef, useState } from "react";
import { UploadCloud, FileSpreadsheet, ArrowRight, ArrowLeft, Save } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TemplateLinks } from "../../pages/ImportCenter";
import { ENTITY_LABEL } from "./ImportHistory";

const STEPS = ["Caricamento", "Anteprima", "Entità", "Mapping", "Analisi"];
const IGNORE = "__ignore__";

export default function ImportWizard({ meta, onDone }) {
  const [step, setStep] = useState(0);
  const [batch, setBatch] = useState(null);
  const [fields, setFields] = useState([]);
  const [presets, setPresets] = useState([]);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { api.get("/import/presets").then(({ data }) => setPresets(data)); }, []);
  useEffect(() => { if (batch?.entity) api.get(`/import/fields/${batch.entity}`).then(({ data }) => setFields(data)); }, [batch?.entity]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/import/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBatch(data); setStep(1);
      toast.success(`${data.row_count} righe rilevate`);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore caricamento"); }
    finally { setBusy(false); }
  };

  const saveConfig = async (patch) => {
    const { data } = await api.put(`/import/batches/${batch.id}/config`, patch);
    setBatch({ ...data, preview: batch.preview });
    return data;
  };

  const applyPreset = async (pid) => {
    const p = presets.find((x) => x.id === pid);
    if (!p) return;
    const norm = (s) => String(s || "").trim().toLowerCase().replace(/[\s_\-./]+/g, " ");
    const pm = Object.fromEntries(Object.entries(p.mapping || {}).map(([k, v]) => [norm(k), v]));
    const mapping = { ...batch.mapping };
    batch.columns.forEach((c) => { if (pm[norm(c)] !== undefined) mapping[c] = pm[norm(c)]; });
    await saveConfig({ mapping, preset_id: pid, source: p.source || batch.source, entity: p.entity || batch.entity });
    toast.success(`Preset "${p.name}" applicato`);
  };

  const savePreset = async () => {
    const name = window.prompt("Nome del nuovo preset");
    if (!name) return;
    await api.post("/import/presets", { name, source: batch.source, entity: batch.entity, mapping: batch.mapping, signature: batch.columns.slice(0, 8) });
    toast.success("Preset salvato");
    api.get("/import/presets").then(({ data }) => setPresets(data));
  };

  const analyze = async () => {
    setBusy(true);
    try {
      await saveConfig({ mapping: batch.mapping, source: batch.source, create_lead: batch.create_lead, lead_defaults: batch.lead_defaults, default_action: "salta" });
      await api.post(`/import/batches/${batch.id}/analyze`);
      onDone(batch.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore analisi"); }
    finally { setBusy(false); }
  };

  const mapped = batch ? Object.values(batch.mapping || {}).filter(Boolean).length : 0;

  return (
    <div data-testid="import-wizard">
      <div className="flex items-center gap-2 mb-5 text-xs">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <span data-testid={`wizard-step-${i}`} className={`w-6 h-6 rounded-full flex items-center justify-center font-mono ${i === step ? "bg-navy-900 text-white" : i < step ? "bg-teal-500 text-white" : "bg-slate-200 text-slate-500"}`}>{i + 1}</span>
            <span className={i === step ? "text-slate-800 font-medium" : "text-slate-400"}>{s}</span>
            {i < STEPS.length - 1 && <span className="w-8 h-px bg-slate-200" />}
          </div>
        ))}
      </div>

      {step === 0 && (
        <div>
          <div data-testid="upload-dropzone" onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files[0]); }} onClick={() => fileRef.current?.click()}
            className={`bg-white rounded-2xl border-2 border-dashed p-12 text-center cursor-pointer transition-colors ${drag ? "border-teal-500 bg-teal-50/40" : "border-slate-300 hover:border-teal-400"}`}>
            <UploadCloud className="w-10 h-10 mx-auto text-teal-600 mb-3" />
            <p className="font-heading font-semibold text-slate-800">{busy ? "Lettura file in corso…" : "Trascina qui il file oppure clicca per selezionarlo"}</p>
            <p className="text-sm text-slate-500 mt-1">Formati: CSV, TSV, XLS, XLSX, JSON · max {meta.max_rows.toLocaleString("it-IT")} righe · 25 MB</p>
            <p className="text-xs text-slate-400 mt-2">Nessun dato viene scritto nel CRM fino alla conferma finale.</p>
            <input ref={fileRef} data-testid="upload-file-input" type="file" accept=".csv,.tsv,.txt,.xls,.xlsx,.json" className="hidden" onChange={(e) => upload(e.target.files[0])} />
          </div>
          <div className="mt-5"><p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Scarica template importazione</p><TemplateLinks entities={meta.entities} /></div>
        </div>
      )}

      {step === 1 && batch && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap gap-6 text-sm" data-testid="file-info">
            <Info l="File" v={batch.filename} icon={FileSpreadsheet} /><Info l="Formato" v={batch.format?.toUpperCase()} /><Info l="Dimensione" v={`${(batch.size_bytes / 1024).toFixed(1)} KB`} />
            <Info l="Righe rilevate" v={batch.row_count} /><Info l="Colonne" v={batch.columns.length} />
            <Info l="Preset riconosciuto" v={batch.suggested_preset_id ? `${presets.find((p) => p.id === batch.suggested_preset_id)?.name || "sì"} (${Math.round(batch.preset_match * 100)}%)` : "nessuno"} />
          </div>
          {batch.read_warnings?.length > 0 && <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800" data-testid="read-warnings">{batch.read_warnings.map((w, i) => <p key={i}>⚠ {w}</p>)}</div>}
          <div className="bg-white rounded-xl border border-slate-200 overflow-auto max-h-[420px] kanban-scroll" data-testid="preview-table">
            <table className="text-xs min-w-full"><thead className="sticky top-0 bg-slate-50"><tr>{batch.columns.map((c) => <th key={c} className="px-3 py-2 text-left font-medium text-slate-600 whitespace-nowrap border-b border-slate-200">{c}</th>)}</tr></thead>
              <tbody>{batch.preview.map((r, i) => <tr key={i} className="border-b border-slate-50">{batch.columns.map((c) => <td key={c} className="px-3 py-1.5 text-slate-700 whitespace-nowrap max-w-[220px] truncate">{String(r[c] ?? "")}</td>)}</tr>)}</tbody></table>
          </div>
          <p className="text-xs text-slate-400">Anteprima delle prime {batch.preview.length} righe su {batch.row_count}.</p>
          <Nav back={() => { setBatch(null); setStep(0); }} next={() => setStep(2)} />
        </div>
      )}

      {step === 2 && batch && (
        <div className="space-y-4">
          <p className="font-heading font-semibold text-slate-800">Cosa vuoi importare?</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="entity-choice">
            {meta.entities.map((e) => (
              <button key={e.key} data-testid={`entity-option-${e.key}`} onClick={() => saveConfig({ entity: e.key })}
                className={`rounded-xl border p-4 text-left transition-colors ${batch.entity === e.key ? "border-teal-500 bg-teal-50/60" : "border-slate-200 bg-white hover:border-teal-300"}`}>
                <p className="font-medium text-slate-800">{e.label}</p>
                <p className="text-xs text-slate-500 mt-1">{e.key === "aziende_contatti" ? "Riconosce o crea l'azienda una sola volta e collega ogni contatto" : e.key === "contatti" ? "Collega all'azienda se presente nel file" : e.key === "lead" ? "Crea lead collegati a contatto/azienda" : e.key === "opportunita" ? "Deal con pipeline/fase" : "Solo anagrafiche aziendali"}</p>
              </button>
            ))}
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 grid sm:grid-cols-2 gap-4 text-sm">
            <div><p className="text-xs text-slate-500 mb-1.5">Source (provenienza dei dati)</p>
              <Select value={batch.source || ""} onValueChange={(v) => saveConfig({ source: v })}><SelectTrigger data-testid="source-select"><SelectValue placeholder="Seleziona source" /></SelectTrigger>
                <SelectContent>{meta.sources.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
            {(batch.entity === "aziende_contatti" || batch.entity === "contatti") && (
              <div><p className="text-xs text-slate-500 mb-1.5">Crea anche un Lead per ogni contatto</p>
                <div className="flex items-center gap-3"><Switch data-testid="create-lead-switch" checked={!!batch.create_lead} onCheckedChange={(v) => setBatch({ ...batch, create_lead: v })} /><span className="text-slate-600">{batch.create_lead ? "Sì (stato Nuovo)" : "No (default)"}</span></div>
                {batch.create_lead && <div className="grid grid-cols-2 gap-2 mt-2">
                  <Input data-testid="lead-service-input" placeholder="Servizio lead" value={batch.lead_defaults?.service || ""} onChange={(e) => setBatch({ ...batch, lead_defaults: { ...batch.lead_defaults, service: e.target.value } })} />
                  <Input data-testid="lead-market-input" placeholder="Mercato lead" value={batch.lead_defaults?.market || ""} onChange={(e) => setBatch({ ...batch, lead_defaults: { ...batch.lead_defaults, market: e.target.value } })} />
                </div>}
              </div>
            )}
          </div>
          <Nav back={() => setStep(1)} next={() => setStep(3)} disabled={!batch.source} />
        </div>
      )}

      {step === 3 && batch && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <p className="font-heading font-semibold text-slate-800">Mappatura colonne → campi CRM ({ENTITY_LABEL[batch.entity]})</p>
            <span className="text-xs text-slate-500">{mapped}/{batch.columns.length} colonne mappate</span>
            <div className="sm:ml-auto flex flex-wrap items-center gap-2 w-full sm:w-auto">
              <Select onValueChange={applyPreset}><SelectTrigger data-testid="preset-select" className="w-full sm:w-64 h-9"><SelectValue placeholder="Applica preset…" /></SelectTrigger>
                <SelectContent>{presets.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select>
              <Button variant="outline" size="sm" data-testid="save-preset-btn" onClick={savePreset} className="gap-1.5"><Save className="w-3.5 h-3.5" /> Salva come preset</Button>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-50" data-testid="mapping-table">
            {batch.columns.map((c) => (
              <div key={c} className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] items-center gap-2 sm:gap-3 px-4 py-2 text-sm">
                <div><p className="font-medium text-slate-800 truncate">{c}</p><p className="text-[11px] text-slate-400 truncate font-mono">es. {String(batch.preview[0]?.[c] ?? "")}</p></div>
                <ArrowRight className="w-4 h-4 text-slate-300 hidden sm:block" />
                <Select value={batch.mapping[c] || IGNORE} onValueChange={(v) => setBatch({ ...batch, mapping: { ...batch.mapping, [c]: v === IGNORE ? null : v } })}>
                  <SelectTrigger data-testid={`mapping-select-${c.replace(/\s+/g, "-")}`} className={`h-9 ${batch.mapping[c] ? "border-teal-300" : "text-slate-400"}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value={IGNORE}>— Ignora colonna —</SelectItem>
                    {fields.map((f) => <SelectItem key={f.key} value={f.key}>{f.ns === "company" ? "Azienda · " : f.ns === "contact" ? "Contatto · " : f.ns === "lead" ? "Lead · " : "Opp · "}{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <Nav back={() => setStep(2)} next={analyze} nextLabel={busy ? "Analisi in corso…" : "Analizza: normalizza, valida, deduplica"} disabled={busy || mapped === 0} />
        </div>
      )}
    </div>
  );
}

function Info({ l, v, icon: Icon }) {
  return <div><p className="text-[11px] text-slate-400 uppercase tracking-wider">{l}</p><p className="font-medium text-slate-800 flex items-center gap-1.5">{Icon && <Icon className="w-4 h-4 text-teal-600" />}{v}</p></div>;
}

function Nav({ back, next, nextLabel = "Continua", disabled }) {
  return (
    <div className="flex justify-between pt-2">
      <Button variant="outline" data-testid="wizard-back-btn" onClick={back} className="gap-1.5"><ArrowLeft className="w-4 h-4" /> Indietro</Button>
      <Button data-testid="wizard-next-btn" onClick={next} disabled={disabled} className="bg-navy-900 hover:bg-navy-700 text-white gap-1.5">{nextLabel} <ArrowRight className="w-4 h-4" /></Button>
    </div>
  );
}
